import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { useIsWideScreen } from '@/app/useIsWideScreen';
import { ApiRequestError } from '@/services/api';
import type { AssignableRole, InvitationIssued } from '@/services/users.service';
import type { BusinessUnit } from '@/types/schemas';
import { v } from '@/theme/tokens';

interface Props {
  roles: AssignableRole[];
  units: BusinessUnit[];
  busy: boolean;
  issued: InvitationIssued | null;
  onSubmit: (input: {
    email: string;
    full_name: string;
    role: string;
    scope_all_units: boolean;
    business_unit_ids: string[];
  }) => void;
  onClose: () => void;
}

const inputStyle = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 9,
  border: `1px solid ${v('line')}`,
  background: v('surface'),
  color: v('ink'),
  fontSize: 13.5,
} as const;

/**
 * «Пригласить пользователя».
 *
 * Two states in one dialog: the form, and — once the invitation exists — the
 * link. The link is shown here because it is the only moment it exists: the
 * server stores a hash, so it cannot be fetched again later, only reissued.
 */
export function InviteUserDialog({ roles, units, busy, issued, onSubmit, onClose }: Props) {
  const wide = useIsWideScreen();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState(roles[0]?.code ?? '');
  const [scopeAll, setScopeAll] = useState(true);
  const [selectedUnits, setSelectedUnits] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!role && roles.length > 0) setRole(roles[0].code);
  }, [roles, role]);

  const toggleUnit = (id: string) => {
    setSelectedUnits((current) =>
      current.includes(id) ? current.filter((u) => u !== id) : [...current, id],
    );
  };

  const submit = () => {
    setError(null);
    if (!fullName.trim()) return setError('Укажите имя');
    if (!email.trim()) return setError('Укажите email');
    if (!role) return setError('Выберите роль');
    if (!scopeAll && selectedUnits.length === 0) {
      return setError('Выберите хотя бы один объект или дайте доступ ко всей компании');
    }
    onSubmit({
      email: email.trim(),
      full_name: fullName.trim(),
      role,
      scope_all_units: scopeAll,
      business_unit_ids: scopeAll ? [] : selectedUnits,
    });
  };

  const copyLink = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard?.writeText(issued.invite_link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div
      className="rt-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: wide ? 'center' : 'stretch',
        justifyContent: 'center',
        padding: wide ? 24 : 0,
        zIndex: 60,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Пригласить пользователя"
    >
      <div
        ref={dialogRef}
        className="rt-dialog"
        style={{
          borderRadius: wide ? 16 : 0,
          padding: 20,
          width: wide ? 520 : '100%',
          // Fullscreen on a phone, a dialog on a desktop.
          maxHeight: wide ? '86vh' : '100vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 14 }}>
          Пригласить пользователя
        </div>

        {issued ? (
          <>
            <div
              style={{
                background: v('positive-soft'),
                color: v('positive'),
                padding: '10px 12px',
                borderRadius: 10,
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              Приглашение создано для {issued.invitation.email}
            </div>

            <div style={{ fontSize: 12, color: v('ink-muted'), marginBottom: 6 }}>
              Ссылка-приглашение
            </div>
            <div
              style={{
                ...inputStyle,
                fontSize: 12,
                wordBreak: 'break-all',
                background: v('panel'),
                marginBottom: 10,
              }}
            >
              {issued.invite_link}
            </div>

            <p style={{ fontSize: 12, color: v('ink-muted'), marginTop: 0 }}>
              Отправьте ссылку вручную — по email, в Telegram или WhatsApp. Ссылка одноразовая и
              действует ограниченное время; показать её повторно нельзя, но можно выпустить новую.
            </p>

            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <Button variant="primary" onClick={() => void copyLink()}>
                {copied ? 'Скопировано' : 'Скопировать ссылку'}
              </Button>
              <Button onClick={onClose}>Готово</Button>
            </div>
          </>
        ) : (
          <>
            <label style={{ display: 'block', marginBottom: 10 }}>
              <span style={{ display: 'block', fontSize: 12, color: v('ink-muted'), marginBottom: 4 }}>
                Имя
              </span>
              <input
                aria-label="Имя"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                style={inputStyle}
              />
            </label>

            <label style={{ display: 'block', marginBottom: 10 }}>
              <span style={{ display: 'block', fontSize: 12, color: v('ink-muted'), marginBottom: 4 }}>
                Email
              </span>
              <input
                aria-label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
              />
            </label>

            <label style={{ display: 'block', marginBottom: 14 }}>
              <span style={{ display: 'block', fontSize: 12, color: v('ink-muted'), marginBottom: 4 }}>
                Роль
              </span>
              <select
                aria-label="Роль"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                style={inputStyle}
              >
                {roles.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>

            <div style={{ fontSize: 12, color: v('ink-muted'), marginBottom: 6 }}>Доступ</div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, fontSize: 13.5 }}>
              <input
                type="radio"
                name="scope"
                checked={scopeAll}
                onChange={() => setScopeAll(true)}
                aria-label="Вся компания"
              />
              Вся компания
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: 13.5 }}>
              <input
                type="radio"
                name="scope"
                checked={!scopeAll}
                onChange={() => setScopeAll(false)}
                aria-label="Выбранные объекты"
              />
              Выбранные объекты
            </label>

            {!scopeAll && (
              <div style={{ paddingLeft: 22, marginBottom: 10 }}>
                {units.length === 0 && (
                  <div style={{ fontSize: 12.5, color: v('ink-muted') }}>Объектов пока нет.</div>
                )}
                {units.map((unit) => (
                  <label
                    key={unit.id}
                    style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0', fontSize: 13 }}
                  >
                    <input
                      type="checkbox"
                      aria-label={unit.name}
                      checked={selectedUnits.includes(unit.id)}
                      onChange={() => toggleUnit(unit.id)}
                    />
                    {unit.name}
                    <span style={{ fontSize: 11.5, color: v('ink-muted') }}>
                      {unit.type === 'PRODUCTION_KITCHEN' ? 'производство' : 'ресторан'}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {error && (
              <div role="alert" style={{ color: v('negative'), fontSize: 12.5, marginTop: 8 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <Button variant="primary" onClick={submit} disabled={busy}>
                {busy ? 'Создание…' : 'Пригласить'}
              </Button>
              <Button onClick={onClose}>Отмена</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Turns a failed invite into the message the owner should actually read. */
export function inviteErrorMessage(error: unknown): string {
  if (!(error instanceof ApiRequestError)) return 'Не удалось создать приглашение';
  return error.message;
}
